import React, { useState, useEffect } from 'react';
import { toastSuccess, toastError } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';

// Parse API response for plan responsibilities
const parsePlanResponsibilities = (apiResponse) => {
  // Use planResponsibilities from API response
  const responsibilities = apiResponse?.planResponsibilities || [];
  const selectedResponsibilities = [];
  const selectedActions = {};
  responsibilities.forEach((res) => {
    if (res.enabled) selectedResponsibilities.push(res._id);
    selectedActions[res._id] = {};
    (res.actions || []).forEach((action) => {
      selectedActions[res._id][action._id] = !!action.enabled;
    });
  });
  return { responsibilities, selectedResponsibilities, selectedActions };
};

function Responsibility() {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [responsibilities, setResponsibilities] = useState([]);
  const [selectedResponsibilities, setSelectedResponsibilities] = useState([]);
  const [selectedActions, setSelectedActions] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);

  // Load plans + default plan responsibilities
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await axios.get('/api/plan');
        const planList = res.data?.plans || [];
        setRoles(planList);
        if (planList.length) {
          const firstPlanId = planList[0]._id;
          setSelectedRole(firstPlanId);
          fetchPlanResponsibilities(firstPlanId);
        }
      } catch {
        toastError('Failed to load plans');
      }
    };
    fetchPlans();
  }, []);

  // Fetch responsibilities for selected plan
  const fetchPlanResponsibilities = async (planId) => {
    try {
      const res = await axios.get(`/api/plan-responsibilities`);
      const {
        responsibilities,
        selectedResponsibilities,
        selectedActions,
      } = parsePlanResponsibilities(res.data);
      setResponsibilities(responsibilities);
      setSelectedResponsibilities(selectedResponsibilities);
      setSelectedActions(selectedActions);
    } catch {
      toastError('Failed to load plan responsibilities');
    }
  };

  // Toggle responsibility
  const handleCheckboxChange = (responsibility, isChecked) => {
    setSelectedResponsibilities((prev) => {
      const set = new Set(prev);
      if (isChecked) {
        set.add(responsibility._id);
        setSelectedActions((prevActions) => {
          const current = { ...prevActions };
          if (!current[responsibility._id]) {
            current[responsibility._id] = {};
          }
          return current;
        });
      } else {
        set.delete(responsibility._id);
        setSelectedActions((prevActions) => {
          const current = { ...prevActions };
          delete current[responsibility._id];
          return current;
        });
      }
      return Array.from(set);
    });
  };

  // Toggle action
  const handleActionChange = (resId, actionId, isChecked) => {
    setSelectedActions((prev) => {
      const resActions = prev[resId] || {};
      return {
        ...prev,
        [resId]: {
          ...resActions,
          [actionId]: isChecked,
        },
      };
    });
  };

  // Save plan responsibilities
  const handleSaveResponsibilities = async () => {
    if (!selectedRole) {
      toastError('Please select a plan first');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        planId: selectedRole,
        responsibilities: selectedResponsibilities.map((resId) => {
          const enabledActionIds = Object.entries(selectedActions[resId] || {})
            .filter(([_, enabled]) => enabled)
            .map(([actionId]) => ({ action: actionId }));
          return { responsibility: resId, actions: enabledActionIds };
        }),
      };
      await axios.post('/api/plan-responsibilities', payload);
      toastSuccess('Plan responsibilities assigned successfully!');
      await fetchPlanResponsibilities(selectedRole);
    } catch (err) {
      console.error('Failed to save plan responsibilities:', err);
      toastError('Failed to save plan responsibilities');
    } finally {
      setSaving(false);
    }
  };

  // Filter responsibilities by search
  const filteredResponsibilities = responsibilities.filter((item) =>
    item.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isResponsibilityAssigned = (responsibility) =>
    selectedResponsibilities.includes(responsibility._id);

  return (
    <div className="container mt-2">
      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0">Responsibility</h5>
        </div>
        
          <div className="d-flex justify-content-between align-items-center p-1 border-bottom">
            <select
              className="form-select w-25"
              value={selectedRole}
              onChange={(e) => {
                setSelectedRole(e.target.value);
                fetchPlanResponsibilities(e.target.value);
              }}
            >
              {roles.map((plan) => (
                <option key={plan._id} value={plan._id}>
                  {plan.name}
                </option>
              ))}
            </select>

            <div className="d-flex gap-2">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: '200px' }}
              />
             {/*  <button
                             className="btn btn-sm btn-success"
                             onClick={handleSaveResponsibilities}
                             disabled={!selectedRole || saving}
                           >
                             {saving ? 'Saving...' : 'Save'}
                           </button> */}
            </div>
          </div>
         <div className="table-responsive">
          <div className="card-body p-0">
          <table className="table table-bordered table-sm mb-0 fs-6">
            <thead className="table-light text-center">
              <tr>
                <th className="px-3">Module Name</th>
                <th className="px-3">Sub Module</th>
                <th className="px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredResponsibilities.length > 0 ? (
                filteredResponsibilities.map((item) => (
                  <tr key={item._id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <input
                          type="checkbox"
                          className="form-check-input ml-0"
                          checked={isResponsibilityAssigned(item)}
                          onChange={(e) =>
                            handleCheckboxChange(item, e.target.checked)
                          }
                          disabled={!selectedRole}
                        />
                        <span className="ml-4">{item.name}</span>
                      </div>
                    </td>
                    <td></td>
                    <td className="text-center">
                      <div className="d-flex flex-column align-items-center gap-1">
                        {(item.actions || []).map((action) => (
                          <div className="form-check" key={action._id}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={`${item._id}_${action._id}`}
                              checked={
                                selectedActions[item._id]?.[action._id] || false
                              }
                              onChange={(e) =>
                                handleActionChange(
                                  item._id,
                                  action._id,
                                  e.target.checked
                                )
                              }
                              disabled={
                                !selectedRole ||
                                !isResponsibilityAssigned(item)
                              }
                            />
                            <label
                              className="form-check-label ms-2"
                              htmlFor={`${item._id}_${action._id}`}
                            >
                              {action.name}
                            </label>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="text-center py-3 text-muted">
                    No responsibilities found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
  );
}

export default Responsibility;
